import React, { useEffect, useRef, useState } from "react";
import {
  CarouselContainer,
  CarouselButton,
  CarouselTrack,
  CarouselTopRightControl,
  CarouselSmallIcon,
  CarouselIcon,
} from "./Carousel.css";
import {
  KeyboardArrowRightIcon,
  KeyboardArrowLeftIcon,
} from "@usap/uicomponents";

const Carousel = ({
  children,
  className,
  classNameLeftButtons,
  classNameRightButtons,
  showLeftButton = true,
  showRightButton = true,
  trackClassName,
  showTopRightControl,
  classNameTopRightButtons,
  hasMdGap = true,
  activeIndex,
  id,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const handleScroll = (scrollOffset: number) => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft + scrollOffset;
      scrollRef.current.scrollTo({
        behavior: "smooth",
        left: scrollLeft,
      });

      setShowLeft(scrollLeft > 0);
      setShowRight(
        scrollLeft < scrollRef.current.scrollWidth - scrollRef.current.clientWidth
      );
    }
  };

  const updateButtonVisibility = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeft(scrollLeft > 0);
      setShowRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    // Ensure button visibility updates after the DOM has rendered
    const timer = setTimeout(() => {
      updateButtonVisibility();
    }, 0);

    return () => clearTimeout(timer);
  }, [children]);

  useEffect(() => {
    // Observe changes in carousel size to adjust button visibility
    const resizeObserver = new ResizeObserver(() => {
      updateButtonVisibility();
    });

    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (activeIndex && id) {
      const contentWidth = document
        ?.getElementById(id.toString())
        ?.getBoundingClientRect()?.width;

      if (scrollRef.current && contentWidth) {
        const newScrollLeft = contentWidth * activeIndex;
        scrollRef.current.scrollLeft = newScrollLeft;
        updateButtonVisibility();
      }
    }
  }, [id, activeIndex]);

  const getContentWidth = () => {
    const contentWidth = id
      ? document?.getElementById(id.toString())?.getBoundingClientRect()?.width
      : 0;

    return contentWidth || 0;
  };

  return (
    <CarouselContainer aria-label="Carousel" className={`carousel-container ${className}`}>
      {showLeftButton && (showRight || showLeft) && (
        <CarouselButton
          className={`carousel-left-button ${classNameLeftButtons} ${
            !showLeft ? "disabled" : ""
          }`}
          onClick={() => handleScroll(-getContentWidth() || -300)}
          aria-label="Left Scroll"
          disabled={!showLeft}
        >
          <CarouselIcon
                icon={KeyboardArrowLeftIcon}
                color="dark-2"
                size="22px"
                aria-hidden="true" 
            />
        </CarouselButton>
      )}
      <CarouselTrack
        aria-label="Carousel Content"
        ref={scrollRef}
        className={`carousel-track ${
          hasMdGap ? "gap-md" : ""
        } ${trackClassName}`}
      >
        {children}
      </CarouselTrack>
      {showRightButton && (showRight || showLeft) && (
        <CarouselButton
          className={`carousel-right-button ${classNameRightButtons} ${
            !showRight ? "disabled" : ""
          }`}
          onClick={() => handleScroll(getContentWidth() || 300)}
          aria-label="Right Scroll"
          disabled={!showRight}
        >
          <CarouselIcon
                icon={KeyboardArrowRightIcon}
                color="dark-2"
                size="22px"
                aria-hidden="true" 
                stroke="currentColor"
            />
        </CarouselButton>
      )}
      {showTopRightControl && (showRight || showLeft) && (
        <CarouselTopRightControl className="carousel-top-right-control">
          <button
            className={`top-right-button ${classNameTopRightButtons}`}
            onClick={() => handleScroll(-getContentWidth() || -300)}
            aria-label="Left Scroll"
          >
            <CarouselSmallIcon
                icon={KeyboardArrowLeftIcon}
                className="carousel-icon-small"
                aria-hidden="true" 
            />
          </button>
          <button
            className={`top-right-button ${classNameTopRightButtons}`}
            onClick={() => handleScroll(getContentWidth() || 300)}
            aria-label="Right Scroll"
          >
             <CarouselSmallIcon
                icon={KeyboardArrowRightIcon}
                className="carousel-icon-small"
                aria-hidden="true" 
            />
          </button>
        </CarouselTopRightControl>
      )}
    </CarouselContainer>
  );
};

export { Carousel };